/**
 * Community Mutations Hook
 * Handles create and update operations for communities with proper cache invalidation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type CommunityResponse,
  type CreateCommunityRequest,
  createCommunity,
  deleteCommunityImage,
  updateCommunity,
  uploadCommunityImage,
} from "../services/community.service"

/**
 * Create Community Mutation Hook
 * Creates a new community and handles:
 * - Cache invalidation for user communities list
 * - Cache invalidation for profile statistics
 * - Returns community response for component to handle navigation
 */
export function useCreateCommunity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCommunityRequest) => createCommunity(data),
    onSuccess: (_result: CommunityResponse | undefined) => {
      // Invalidate user communities list
      queryClient.invalidateQueries({
        queryKey: ["userCommunities"],
      })

      // Invalidate profile statistics (communities count)
      queryClient.invalidateQueries({
        queryKey: ["profileStatistics"],
      })

      // Return community response for component to handle navigation
      // Navigation is handled by the component showing the success modal
    },
  })
}

export function useUpdateCommunity(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Parameters<typeof updateCommunity>[1]) => updateCommunity(communityId, data),
    onSuccess: () => {
      // Invalidate the specific community detail
      queryClient.invalidateQueries({
        queryKey: ["community", communityId],
      })

      // Invalidate user communities list (might show in user's communities)
      queryClient.invalidateQueries({
        queryKey: ["userCommunities"],
      })

      // Invalidate community members
      queryClient.invalidateQueries({
        queryKey: ["communityMembers", communityId],
      })

      // Invalidate community ritualists
      queryClient.invalidateQueries({
        queryKey: ["communityRitualists", communityId],
      })
    },
  })
}

export function useUploadCommunityImage(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => uploadCommunityImage(communityId, file),
    onSuccess: () => {
      // Invalidate community detail to show new image
      queryClient.invalidateQueries({
        queryKey: ["community", communityId],
      })

      // Invalidate user communities list
      queryClient.invalidateQueries({
        queryKey: ["userCommunities"],
      })
    },
  })
}

export function useDeleteCommunityImage(communityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (uploadId: string) => deleteCommunityImage(communityId, uploadId),
    onSuccess: () => {
      // Invalidate community detail to reflect image removal
      queryClient.invalidateQueries({
        queryKey: ["community", communityId],
      })

      // Invalidate user communities list
      queryClient.invalidateQueries({
        queryKey: ["userCommunities"],
      })
    },
  })
}
